/**
 * ProductTargetGroupsTab - Manage target groups and nested targets for a product
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  TextField,
  Chip,
  Collapse,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import productsService, {
  TargetGroup,
  TargetGroupCreate,
  DefaultTarget,
} from '../services/products';
import TargetDialog, { type ActiveCalibration } from './product/TargetDialog';
import { resolveCurve } from '../utils/regressionConversion';
import { useCompanyStrengthUnit } from '../utils/useCompanyStrengthUnit';
import {
  TARGET_OUTSIDE_RANGE_LABEL,
  knownMaturityMeta,
  labelWithUnit,
  maturityTargetLabel,
  targetRangeFlag,
} from '../utils/maturityDisplay';
import { hasPermission } from '../utils/userPermissions';
import type { CalibratedRange } from '../services/pours';
import { useAuth } from '../context/AuthContext';
import { apiErrorText } from '../utils/apiError';
import { SectionActions } from './product/SectionCard';

interface ProductTargetGroupsTabProps {
  productId: string;
  /** The mix's default target group (ProductInfo.TargetGroupid): listed first, marked "default". */
  defaultGroupId?: string | null;
  /** The mix design's maturity method ("Nurse Saul" | "Arrhenius"); names
   *  the target's maturity unit. Null or absent when not known. */
  maturityMethod?: string | null;
  /** Arrhenius specified temperature (names "Equivalent Age at Ts °C"). */
  specifiedTemp?: number | null;
  /** The mix design company's strength unit (MPa / Psi / N/mm²); null while
   *  it loads or when it cannot be read. When the prop is absent the tab
   *  falls back to the signed-in user's company. */
  strengthUnit?: string | null;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const ProductTargetGroupsTab: React.FC<ProductTargetGroupsTabProps> = ({
  productId,
  maturityMethod,
  specifiedTemp,
  defaultGroupId,
  strengthUnit: strengthUnitProp,
  onError,
  onSuccess,
}) => {
  const [groups, setGroups] = useState<TargetGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [targets, setTargets] = useState<Record<string, DefaultTarget[]>>({});

  // Group dialog state
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<TargetGroup | null>(null);
  const [groupName, setGroupName] = useState('');

  // Target dialog state
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [editingTarget, setEditingTarget] = useState<DefaultTarget | null>(null);
  const [targetGroupId, setTargetGroupId] = useState('');

  // The product's active calibration (drives EA auto-calculation in the dialog)
  const [activeCalibration, setActiveCalibration] = useState<ActiveCalibration | null>(null);
  // Its tested range: target strengths outside it are flagged (allowed).
  const [testedRange, setTestedRange] = useState<CalibratedRange | null>(null);

  // Strength display unit from the mix design company's StrengthFormat, as
  // the host page passes it; only without it is the user's company read.
  const { user } = useAuth();
  // Mirrors the backend gates (utils/rbac.py strength_targets.*).
  const canCreateGroup = hasPermission(user, 'strength_targets.create_group');
  const canEdit = hasPermission(user, 'strength_targets.edit');
  const canDelete = hasPermission(user, 'strength_targets.delete');
  const maturityMeta = knownMaturityMeta(maturityMethod, specifiedTemp);
  const maturityColumn = maturityMeta
    ? `${maturityMeta.index_label} target (${maturityMeta.unit})`
    : maturityTargetLabel(maturityMethod);
  const hasUnitProp = strengthUnitProp !== undefined;
  const ownStrengthUnit = useCompanyStrengthUnit(hasUnitProp ? null : user?.CompanyID);
  const strengthUnit = hasUnitProp ? strengthUnitProp : ownStrengthUnit;

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<'group' | 'target'>('group');
  const [deleteId, setDeleteId] = useState('');
  const [deleteGroupId, setDeleteGroupId] = useState('');

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productsService.listTargetGroups(productId);
      setGroups(data);
    } catch (err: any) {
      onError(apiErrorText(err, 'Failed to load target groups'));
    } finally {
      setLoading(false);
    }
  }, [productId, onError]);

  const loadActiveCalibration = useCallback(async () => {
    try {
      const links = await productsService.listProductCalibrations(productId);
      const active = links.find((l) => l.is_active);
      setTestedRange(active?.summary?.calibrated_range ?? null);
      if (!active) {
        setActiveCalibration(null);
        return;
      }
      // Fetch the calibration detail for its chosen curve + coefficients
      // (drives EA auto-calc).
      const cal = await productsService.getCalibration(active.calibration_id);
      const { curveType, a, b } = resolveCurve(cal);
      setActiveCalibration({
        id: active.calibration_id,
        name: active.calibration_name || active.calibration_code || cal.name || 'Calibration',
        curveType,
        regressionA: a,
        regressionB: b,
      });
    } catch {
      // Non-fatal: dialog falls back to manual EA entry when no calibration is known.
      setActiveCalibration(null);
    }
  }, [productId]);

  useEffect(() => {
    loadGroups();
    loadActiveCalibration();
  }, [loadGroups, loadActiveCalibration]);

  const loadTargets = async (groupId: string) => {
    try {
      const data = await productsService.listTargets(productId, groupId);
      setTargets(prev => ({ ...prev, [groupId]: data }));
    } catch (err: any) {
      onError(apiErrorText(err, 'Failed to load targets'));
    }
  };

  const handleToggleGroup = (groupId: string) => {
    if (expandedGroup === groupId) {
      setExpandedGroup(null);
    } else {
      setExpandedGroup(groupId);
      if (!targets[groupId]) {
        loadTargets(groupId);
      }
    }
  };

  // Group CRUD
  const handleOpenGroupDialog = (group?: TargetGroup) => {
    if (group) {
      setEditingGroup(group);
      setGroupName(group.target_group_name || '');
    } else {
      setEditingGroup(null);
      setGroupName('');
    }
    setGroupDialogOpen(true);
  };

  const handleSaveGroup = async () => {
    if (!groupName.trim()) {
      onError('Group name is required');
      return;
    }

    setLoading(true);
    try {
      const data: TargetGroupCreate = { target_group_name: groupName.trim() };
      if (editingGroup) {
        await productsService.updateTargetGroup(productId, editingGroup.target_group_id, data);
        onSuccess('Target group updated');
      } else {
        await productsService.createTargetGroup(productId, data);
        onSuccess('Target group created');
      }
      setGroupDialogOpen(false);
      loadGroups();
    } catch (err: any) {
      onError(apiErrorText(err, 'Failed to save target group'));
    } finally {
      setLoading(false);
    }
  };

  // Target CRUD — the add/edit form and its auto-calculation live in TargetDialog.
  const handleOpenTargetDialog = (groupId: string, target?: DefaultTarget) => {
    setTargetGroupId(groupId);
    setEditingTarget(target ?? null);
    setTargetDialogOpen(true);
  };

  // Delete handlers
  const handleDeleteClick = (type: 'group' | 'target', id: string, groupId?: string) => {
    setDeleteType(type);
    setDeleteId(id);
    setDeleteGroupId(groupId || '');
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      if (deleteType === 'group') {
        await productsService.deleteTargetGroup(productId, deleteId);
        onSuccess('Target group deleted');
        loadGroups();
      } else {
        await productsService.deleteTarget(productId, deleteGroupId, deleteId);
        onSuccess('Target deleted');
        loadTargets(deleteGroupId);
      }
      setDeleteConfirmOpen(false);
    } catch (err: any) {
      onError(apiErrorText(err, `Failed to delete ${deleteType}`));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Section title comes from the host page; the button goes in its card header */}
      <SectionActions>
        {canCreateGroup && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenGroupDialog()}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            Add group
          </Button>
        )}
      </SectionActions>

      {groups.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No strength targets yet. Add a group, then the strengths pour setup should track in it.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Group</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...groups]
                .sort((a, b) => Number(b.target_group_id === defaultGroupId) - Number(a.target_group_id === defaultGroupId))
                .map((group) => (
                <React.Fragment key={group.target_group_id}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleToggleGroup(group.target_group_id)}
                  >
                    <TableCell>
                      <IconButton
                        size="small"
                        aria-label={expandedGroup === group.target_group_id ? 'hide targets' : 'show targets'}
                        aria-expanded={expandedGroup === group.target_group_id}
                        onClick={(e) => { e.stopPropagation(); handleToggleGroup(group.target_group_id); }}
                      >
                        {expandedGroup === group.target_group_id ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {group.target_group_name}
                        </Typography>
                        {group.target_group_id === defaultGroupId && (
                          <Chip size="small" label="Default" color="primary" variant="outlined" sx={{ height: 20 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {group.create_date ? new Date(group.create_date).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {canEdit && (
                        <IconButton
                          size="small"
                          aria-label="rename group"
                          onClick={(e) => { e.stopPropagation(); handleOpenGroupDialog(group); }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="delete group"
                          onClick={(e) => { e.stopPropagation(); handleDeleteClick('group', group.target_group_id); }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expanded targets */}
                  <TableRow>
                    {/* "&&" beats the theme's phone cell padding, so a closed group leaves no gap */}
                    <TableCell colSpan={4} sx={{ '&&': { p: 0 }, border: 0 }}>
                      <Collapse in={expandedGroup === group.target_group_id}>
                        <Box sx={{ pl: { xs: 2, sm: 7 }, pr: 2, py: 2, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">Targets</Typography>
                            {canEdit && (
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenTargetDialog(group.target_group_id)}
                              >
                                Add target
                              </Button>
                            )}
                          </Box>
                          {(!targets[group.target_group_id] || targets[group.target_group_id].length === 0) ? (
                            <Typography variant="body2" color="text.secondary">
                              No targets in this group yet.
                            </Typography>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Purpose</TableCell>
                                  <TableCell>{maturityColumn}</TableCell>
                                  <TableCell>{labelWithUnit('Strength + margin', strengthUnit)}</TableCell>
                                  <TableCell>Safety margin (%)</TableCell>
                                  <TableCell>{labelWithUnit('Base strength', strengthUnit)}</TableCell>
                                  <TableCell align="right">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {targets[group.target_group_id].map((target) => (
                                  <TableRow key={target.target_id}>
                                    <TableCell>{target.purpose}</TableCell>
                                    <TableCell>{target.target ?? '-'}</TableCell>
                                    <TableCell>
                                      {target.strength ?? '-'}
                                      {targetRangeFlag(target.strength, testedRange) && (
                                        <Chip
                                          size="small"
                                          color="warning"
                                          variant="outlined"
                                          sx={{ ml: 1, height: 20 }}
                                          label={TARGET_OUTSIDE_RANGE_LABEL}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>{target.safety_margin ?? '-'}</TableCell>
                                    <TableCell>{target.base_strength ?? '-'}</TableCell>
                                    <TableCell align="right">
                                      {canEdit && (
                                        <IconButton
                                          size="small"
                                          aria-label="edit target"
                                          onClick={() => handleOpenTargetDialog(group.target_group_id, target)}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                      {canDelete && (
                                        <IconButton
                                          size="small"
                                          color="error"
                                          aria-label="delete target"
                                          onClick={() => handleDeleteClick('target', target.target_id, group.target_group_id)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Group Dialog */}
      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroup ? 'Edit Target Group' : 'Add Target Group'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mt: 2 }}
            inputProps={{ maxLength: 100 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveGroup} variant="contained" disabled={loading}>
            {editingGroup ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Target Dialog (legacy-style layout with EA auto-calculation) */}
      <TargetDialog
        open={targetDialogOpen}
        productId={productId}
        groupId={targetGroupId}
        editing={editingTarget}
        calibration={activeCalibration}
        strengthUnit={strengthUnit}
        maturityMethod={maturityMethod}
        specifiedTemp={specifiedTemp}
        testedRange={testedRange}
        onClose={() => setTargetDialogOpen(false)}
        onSuccess={onSuccess}
        onError={onError}
        onSaved={() => loadTargets(targetGroupId)}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this {deleteType}?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductTargetGroupsTab;
